import { defineStore } from 'pinia';
import router from '@/router';
import { authAPI } from '@/api';

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    user: null,
    encodeData: null,
    userName: null,
    userPhone: null,
    version: localStorage.getItem('version') || null,
    socketUrl: localStorage.getItem('socketUrl') || null, // 로그인한 서버의 Socket URL 저장
    serverId: localStorage.getItem('serverId') || null, // socketUrl에서 추출한 serverId
    menuPermissions: JSON.parse(localStorage.getItem('menuPermissions') || '{}'), // 직원계정 메뉴 권한
    isEmployeeAccount: localStorage.getItem('isEmployeeAccount') === 'true' // 직원계정 여부
  }),
  actions: {
    refreshToken: '',
    async setupUser() {
      let response = await authAPI.GetUser();
      if (response.status === 'Success') {
        this.user = response.data;
      }
      return this.user;
    },

    async login(userId, password,siteUrl) {
      const responseModel = await authAPI.Login({
        userId: userId,
        password: password,
        siteUrl : siteUrl
      });

      const host = window.location.hostname;
      /*if (host.includes('crosssell.co.kr')) {
        await authAPI.LoginStock({
          userId: userId,
          password: password,
          siteUrl: siteUrl
        });
      }*/

      if (responseModel.status === 'Success') {
        this.user = responseModel.data;
        this.accessToken = responseModel.data.accessToken;
        this.refreshToken = responseModel.data.refreshToken;
        this.version = responseModel.data.version || responseModel.version || null;
        // 로그인 응답에서 socketUrl이 있으면 저장 (로드밸런싱 대응)
        // SocketUrl (대문자) 또는 socketUrl (소문자) 모두 지원
        const socketUrl = responseModel.data.SocketUrl || responseModel.data.socketUrl;
        if (socketUrl) {
          this.socketUrl = socketUrl;
          localStorage.setItem('socketUrl', this.socketUrl);
          console.log('[Auth] 서버에서 Socket URL 수신:', socketUrl);
          
          // socketUrl에서 serverId 추출
          try {
            const urlObj = new URL(socketUrl);
            const serverId = urlObj.searchParams.get('serverId');
            if (serverId) {
              localStorage.setItem('serverId', serverId);
              console.log('[Auth] ServerId 추출:', serverId);
            }
          } catch (error) {
            console.warn('[Auth] ServerId 추출 실패:', error);
          }
        }

        localStorage.setItem('user', JSON.stringify(this.user)); // 2025-02-21 추가 - 새로고침시 유저정보 누락 현상 제거 
        localStorage.setItem('accessToken', this.accessToken);
        localStorage.setItem('refreshToken', this.refreshToken);
        if (this.version) {
          localStorage.setItem('version', this.version);
        }
        // 본계정 로그인 시 직원계정 플래그 제거
        localStorage.removeItem('isEmployeeAccount');
        localStorage.removeItem('subUserId');
        localStorage.removeItem('menuPermissions');
        this.menuPermissions = {};
        this.isEmployeeAccount = false; // 본계정으로 설정
      }
      return responseModel;
    },

    async loginEmployee(mainUserId, subUserId, subPassword, siteUrl) {
      const responseModel = await authAPI.LoginEmployee({
        mainUserId: mainUserId,
        subUserId: subUserId,
        subPassword: subPassword,
        siteUrl: siteUrl
      });

      if (responseModel.status === 'Success') {
        this.user = responseModel.data;
        this.accessToken = responseModel.data.accessToken;
        this.refreshToken = responseModel.data.refreshToken;
        this.version = responseModel.data.version || responseModel.version || null;
        
        const socketUrl = responseModel.data.SocketUrl || responseModel.data.socketUrl;
        if (socketUrl) {
          this.socketUrl = socketUrl;
          localStorage.setItem('socketUrl', this.socketUrl);
          console.log('[Auth] 서버에서 Socket URL 수신:', socketUrl);
          
          try {
            const urlObj = new URL(socketUrl);
            const serverId = urlObj.searchParams.get('serverId');
            if (serverId) {
              localStorage.setItem('serverId', serverId);
              console.log('[Auth] ServerId 추출:', serverId);
            }
          } catch (error) {
            console.warn('[Auth] ServerId 추출 실패:', error);
          }
        }

        localStorage.setItem('user', JSON.stringify(this.user));
        localStorage.setItem('accessToken', this.accessToken);
        localStorage.setItem('refreshToken', this.refreshToken);
        if (this.version) {
          localStorage.setItem('version', this.version);
        }
        // 직원계정 정보 저장
        // 응답 데이터에서 subUserID (대문자) 또는 subUserId (소문자) 모두 지원
        const subUserId = responseModel.data.subUserID || responseModel.data.subUserId;
        if (responseModel.data && subUserId) {
          localStorage.setItem('subUserId', subUserId);
          localStorage.setItem('isEmployeeAccount', 'true');
          this.isEmployeeAccount = true; // 직원계정으로 설정
          
          // 권한 정보 저장
          if (responseModel.data.menuPermissions) {
            this.menuPermissions = responseModel.data.menuPermissions;
            localStorage.setItem('menuPermissions', JSON.stringify(this.menuPermissions));
            console.log('[Auth] 직원계정 권한 저장:', this.menuPermissions);
          } else {
            console.warn('[Auth] menuPermissions가 응답에 없습니다.');
          }
        } else {
          console.warn('[Auth] subUserID/subUserId가 응답에 없습니다.');
        }
      }
      return responseModel;
    },

    logout() {
      this.accessToken = null;
      this.refreshToken = null;
      this.user = null;
      this.version = null;
      this.socketUrl = null;
      this.serverId = null;

      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('version');
      localStorage.removeItem('loginUserId');
      localStorage.removeItem('subUserId');
      localStorage.removeItem('isEmployeeAccount');
      localStorage.removeItem('socketUrl');
      localStorage.removeItem('serverId');
      localStorage.removeItem('menuPermissions');
      this.menuPermissions = {};
      this.isEmployeeAccount = false; // 로그아웃 시 초기화
      if (router.history.current.fullPath !== '/user') {
        router.push('/user');
      }
    },
    restoreSession() {  // 2025-02-21 추가 - 새로고침시 유저정보 누락 현상 제거
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        this.user = JSON.parse(storedUser);
        this.accessToken = localStorage.getItem("accessToken");
        this.refreshToken = localStorage.getItem("refreshToken");
        this.version = localStorage.getItem("version");
        this.hiddenSite = localStorage.getItem("hiddenSite");
        this.socketUrl = localStorage.getItem("socketUrl");
        this.serverId = localStorage.getItem("serverId");
        
        // 직원계정 여부 확인 및 권한 정보 복원
        const isEmployeeAccount = localStorage.getItem("isEmployeeAccount") === 'true';
        this.isEmployeeAccount = isEmployeeAccount; // state에 복원
        if (isEmployeeAccount) {
          const storedPermissions = localStorage.getItem("menuPermissions");
          if (storedPermissions) {
            try {
              this.menuPermissions = JSON.parse(storedPermissions);
              console.log('[Auth] 세션 복원 - 직원계정 권한:', this.menuPermissions);
            } catch (e) {
              console.error("Failed to parse menuPermissions:", e);
              this.menuPermissions = {};
            }
          } else {
            console.warn('[Auth] 세션 복원 - menuPermissions가 localStorage에 없습니다.');
            this.menuPermissions = {};
          }
        } else {
          // 본계정인 경우 권한 정보 초기화
          this.menuPermissions = {};
        }
      }
    },
    getStoredUser() {
      try {
        return JSON.parse(localStorage.getItem("user")) || null;
      } catch (error) {
        console.error("Failed to parse stored user:", error);
        return null;
      }
    },
    setEncodeData(data) {
      this.encodeData = data;
      router.push('/user/join');
    },
    setUserInfo(info) {
      this.userName = info.name;
      this.userPhone = info.phone;
    },
    // 권한 체크 메서드
    hasPermission(menuKey, permissionType) {
      // 본계정은 권한과 상관없이 모든 기능 허용
      if (!this.isEmployeeAccount) {
        return true;
      }
      
      // 직원계정은 권한 체크
      if (!this.menuPermissions || Object.keys(this.menuPermissions).length === 0) {
        console.warn('[Auth] menuPermissions가 비어있습니다:', { menuKey, permissionType, menuPermissions: this.menuPermissions });
        return false;
      }
      
      // menuKey가 없거나 빈 문자열이면 false 반환
      if (!menuKey || menuKey === '') {
        console.warn('[Auth] menuKey가 없습니다:', { menuKey, permissionType });
        return false;
      }
      
      if (!this.menuPermissions[menuKey]) {
        console.warn('[Auth] 메뉴 키가 권한에 없습니다:', { menuKey, permissionType, availableKeys: Object.keys(this.menuPermissions) });
        return false;
      }
      
      const menuPerms = this.menuPermissions[menuKey];
      const hasPerm = menuPerms[permissionType] === true;
      console.log('[Auth] 권한 체크 결과:', { menuKey, permissionType, hasPerm, menuPerms });
      return hasPerm;
    },
    // 읽기 권한 체크
    canRead(menuKey) {
      return this.hasPermission(menuKey, 'read');
    },
    // 쓰기 권한 체크
    canWrite(menuKey) {
      return this.hasPermission(menuKey, 'write');
    },
    // 수정 권한 체크
    canEdit(menuKey) {
      return this.hasPermission(menuKey, 'edit');
    },
    // 삭제 권한 체크
    canDelete(menuKey) {
      return this.hasPermission(menuKey, 'delete');
    }
  },
  getters: {
    // isEmployeeAccount는 이제 state에 있으므로 getter 제거
  }
});
